import React, { memo, Suspense, useState } from 'react';
import { Button, Dropdown, message } from 'antd';
import { ReactComponent as DeleteSvg } from '@assets/icons/delete.svg';
import { ReactComponent as DownloadInvoiceSvg } from '@assets/icons/download-invoice.svg';
import { ReactComponent as ActionSvg } from '@assets/icons/action.svg';
import { ReactComponent as SuccessSvg } from '@assets/icons/success.svg';
import { downloadAll } from '@helpers/download';
import {
  QUERY_ASSET_REVIEW_CONTENT,
  useUpdateAssetReviewMutation
} from '@hooks/queries/assetReview';
import { AssetItemDto } from '@api/Api';
import ConfirmModal from '@components/Modals/ConfirmModal';

type ActionFolderProps = {
  canManageReviews: boolean;
  assets: AssetItemDto[];
  folderName: string;
  reviewId: string;
  folderId: string;
};

export default memo(function ActionFolder({
  canManageReviews,
  assets,
  folderName,
  reviewId,
  folderId
}: ActionFolderProps) {
  const [isShowConfirmModal, setIsShowConfirmModal] = useState(false);

  const { mutateAsync: updateAssetReview, isPending: isUpdatingAssetReview } =
    useUpdateAssetReviewMutation({
      queryKey: [QUERY_ASSET_REVIEW_CONTENT]
    });

  const onRemoveFolderFromReview = async () => {
    try {
      setIsShowConfirmModal(false);
      await updateAssetReview({
        reviewId,
        folders: {
          remove: [folderId]
        }
      });
      message.success({
        key: 'asset-removed-from-review',
        content: `${folderName} has been removed from the shared link.`,
        className: 'message-dark-modal',
        icon: <SuccessSvg className="anticon" />,
        duration: 2
      });
    } catch (error) {
      console.error(error);
    }
  };

  if (canManageReviews) {
    return (
      <>
        <Dropdown
          menu={{
            onClick: ({ domEvent }) => domEvent.stopPropagation(),
            items: [
              {
                key: 'download',
                icon: <DownloadInvoiceSvg className="b-opacity-icon" />,
                disabled: !assets?.length,
                label: 'Download',
                onClick: () => {
                  if (!assets?.length) return;
                  downloadAll(assets, `${folderName}.zip`, folderId);
                }
              },
              {
                key: 'delete',
                icon: <DeleteSvg className="b-opacity-icon" />,
                label: 'Remove from shared link',
                onClick: () => {
                  setIsShowConfirmModal(true);
                }
              }
            ]
          }}
          overlayClassName="asset_links_overlay"
          placement="bottomLeft"
          trigger={['click']}
        >
          <Button
            onClick={(e) => e.stopPropagation()}
            type="text"
            size="small"
            loading={isUpdatingAssetReview}
            style={{
              padding: 0,
              width: 24,
              height: 24,
              marginLeft: 'auto'
            }}
          >
            <ActionSvg style={{ marginRight: 0 }} />
          </Button>
        </Dropdown>
        <Suspense fallback={null}>
          <div onClick={(e) => e.stopPropagation()}>
            <ConfirmModal
              visible={isShowConfirmModal}
              onConfirm={onRemoveFolderFromReview}
              onCancel={() => setIsShowConfirmModal(false)}
              okText="Remove"
              title="Remove folder from shared link"
              description="Are you sure you want to remove this folder from the shared link?"
            />
          </div>
        </Suspense>
      </>
    );
  }

  return (
    <Button
      type="text"
      size="small"
      className="campaign-review-btn-download"
      disabled={!assets?.length}
      icon={<DownloadInvoiceSvg />}
      onClick={(e) => {
        e.stopPropagation();
        if (!assets?.length) return;
        downloadAll(assets, `${folderName}.zip`, folderId);
      }}
    />
  );
});
